import { useContext, useMemo } from 'react';
import { countryToISO } from 'vopay';
import { SubAccountListHostProps } from 'shared/subAccountList';
import AccountContext from '@/components/Entities/Accounts/AccountContext';
import accountPageLocation from '@/components/Entities/Accounts/accountPageLocation';
import usePermissionLabels from '@/hooks/usePermissionLabels';
import { usePartnerData } from '@/providers/PartnerData.provider';
import adaptSubAccountPermissions from './adaptSubAccountPermissions';
import useAccountSubAccountPermissions from './useAccountSubAccountPermissions';

/**
 * The one place partner-dashboard maps its account model onto what the shared Sub-Account List takes
 * as props (ID-13430). Mounted by the account page's Sub-Accounts tab under the account-scoped api, so
 * the row Permissions drawer's fetch goes out as the account. Mirrors client-dashboard's
 * useSubAccountListHostProps, minus what this milestone has no surface for: adding, editing,
 * transferring, blocking, deactivating and the User Management / Approval Workflow drawers all stay
 * off. The permissions drawer edits for every role but Viewer, which gets it read-only.
 */
export default function useAccountSubAccountsHostProps(accountId: string): SubAccountListHostProps {
  const { details } = useContext(AccountContext);

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  // The listing sends the country as its display name; the list keys its default currency on ISO.
  const country = countryToISO(details[accountId]?.address.country ?? '');

  // The row Permissions drawer's data. Adapted at render, not at fetch, so the labels follow a language
  // switch; the partner's account-permission labels cover the same permission codes.
  const {
    fetchingPermissions,
    permissions: rawPermissions,
    fetchSubAccountPermissions,
    saveSubAccountPermissions,
  } = useAccountSubAccountPermissions();

  const { resolvePermissionLabel, resolveSectionLabel } = usePermissionLabels();

  const permissionSections = useMemo(
    () =>
      adaptSubAccountPermissions(rawPermissions, {
        resolveItemLabel: resolvePermissionLabel,
        resolveSectionLabel,
      }),
    [rawPermissions, resolvePermissionLabel, resolveSectionLabel],
  );

  const hostProps = useMemo<Omit<SubAccountListHostProps, 'permissionsEditor'>>(
    () => ({
      // The partner listing does not carry the account type; blocking is not offered here anyway.
      account: { accountID: accountId, accountType: '', country },
      // Permissions are the one row action this milestone offers: editable for Admin and Support, read-only
      // for Viewer (the scoped api refuses a Viewer's save besides). Adding, editing, transferring, blocking
      // and deactivating stay off for every role.
      userPermissions: {
        canEditPermissions: !isRoleViewOnly,
        canDeactivateSubAccounts: false,
        canAddSubaccounts: false,
        canManageBlockedAccounts: false,
        canCreateAccountTransfer: false,
      },
      hostPaths: {
        // A sub-account opens on the same account page as its parent, which fetches what it shows.
        subAccountDetail: (subAccountID: string, currency: string) => {
          const { pathname, search } = accountPageLocation({ AccountID: subAccountID, Currency: currency });

          return `${pathname}?${search}`;
        },
      },
      hostActions: {},
    }),
    [accountId, country, isRoleViewOnly],
  );

  // Outside the memo: the hook's fetch / save aren't referentially stable, and the drawer only reads
  // them on open.
  return {
    ...hostProps,
    permissionsEditor: {
      sections: permissionSections,
      fetchingPermissions,
      fetchPermissions: async (subAccountID) => {
        await fetchSubAccountPermissions({ SubaccountID: subAccountID });
      },
      savePermissions: async (subAccountID, values) => {
        await saveSubAccountPermissions({ SubaccountID: subAccountID, ...values });
      },
    },
  };
}
